/**
 * Download Center: one queue for every download in the app, owned above the
 * router, the mirror of the Upload Center.
 *
 * Why not a zip. A bundle has to be built somewhere before the first byte reaches
 * the person, it is capped, and it takes longer to build than to download. Files
 * come down one at a time instead, in the order they were picked, each straight
 * from Cloud Storage, and each named so that it says where it came from:
 * "MS-3-2026-011 - Review Dr Shimaa - raw - Reviews - C0198.MP4".
 *
 * Two ways of saving, decided by what the browser can do, never by a setting:
 *
 *  - folder: on a desktop Chromium the person picks a folder once and every file
 *    streams into it. Real bytes, real progress, speed and time left, cancel in
 *    flight, nothing held in memory.
 *  - browser: everywhere else (Safari, iOS, Android, Firefox) each file is handed
 *    to the browser's own download manager in turn. That manager shows the bytes;
 *    this queue shows the order and what has been handed over.
 *
 * Started strictly in the order picked. Into a folder, up to FOLDER_LANES files run
 * at once (2026-09-26): most raws are 2 to 13 MB photos, and one at a time spent more
 * of each file waiting for the first byte than moving bytes. A single big clip still
 * gets the whole line whenever it is the only one left. Handing files to the
 * browser's own downloads stays one at a time; that manager runs them itself.
 *
 * Survives the tab (Sarah, 2026-11-04: an 88 GB event pull ran all day, the laptop froze,
 * and the list came back empty "as if nothing happened"). The queue and the picked folder
 * are kept in IndexedDB; after a reload whatever was waiting or moving comes back paused,
 * and Resume carries on into the same folder, finished files skipped, a half file
 * continued from its last byte. Each file also asks for a fresh signed link just before
 * it starts: the link the list handed out expires after 6 to 12 hours.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { reportTransfer } from '@/lib/transfer-stats';
import { api } from '@/lib/api';

export type DlState = 'queued' | 'downloading' | 'paused' | 'handed' | 'done' | 'failed' | 'cancelled';
export type DlMode = 'folder' | 'browser';

export interface DownloadJob {
  id: string;
  assetId: string;
  creativeId: string;
  creativeCode: string;
  creativeTitle: string;
  kind: string;
  category?: string;
  /** the name the file is saved under, already descriptive */
  name: string;
  size: number;
  url: string;
  state: DlState;
  pct: number;
  loaded: number;
  bps: number | null;
  error?: string;
  queuedAt: number;
  startedAt?: number;
  finishedAt?: number;
  batch: number;
  mode: DlMode;
  /** the file name settled on at the first attempt, so a retry continues the same file */
  savedName?: string;
  /** storage's ETag of the object, so a resume never stitches two versions together */
  etag?: string;
  attempts: number;
  /** a backing-off retry must not start before this moment */
  notBefore?: number;
  /** brought back from an earlier session: waits for Resume */
  restored?: boolean;
}

export interface EnqueueDownload {
  assetId: string;
  creativeId: string;
  creativeCode?: string;
  creativeTitle?: string;
  kind: string;
  category?: string;
  name: string;
  size: number;
  url: string;
}

// The File System Access API is Chromium-only and not in every TS lib.
interface FSWritable { write(chunk: Uint8Array): Promise<void>; seek(pos: number): Promise<void>; close(): Promise<void>; abort(): Promise<void> }
interface FSPermission { requestPermission?(opts: { mode: 'readwrite' }): Promise<'granted' | 'denied' | 'prompt'> }
interface FSFileHandle { createWritable(opts?: { keepExistingData?: boolean }): Promise<FSWritable>; getFile(): Promise<{ size: number }> }
export interface FSDirHandle extends FSPermission {
  name: string;
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<FSFileHandle>;
  removeEntry(name: string): Promise<void>;
}
type PickerWindow = Window & { showDirectoryPicker?: (opts?: { mode?: 'readwrite' | 'read' }) => Promise<FSDirHandle> };

interface Ctx {
  jobs: DownloadJob[];
  active: number;
  queued: number;
  paused: number;
  failed: number;
  done: number;
  pendingBytes: number;
  overallPct: number | null;
  doneThisRun: number;
  totalThisRun: number;
  bytesPerSec: number | null;
  etaSeconds: number | null;
  /** the folder files are streaming into, when the browser can do that */
  folderName: string | null;
  /** true on a browser that can save straight into a folder the person picks */
  canPickFolder: boolean;
  /** ask for a folder. Must be called from a click. null = the person cancelled. */
  pickFolder: () => Promise<FSDirHandle | null>;
  enqueue: (items: EnqueueDownload[], dir?: FSDirHandle | null) => number;
  retry: (id: string) => void;
  retryAllFailed: () => void;
  cancel: (id: string) => void;
  /** stop and keep the part on disk; resume carries on from it */
  pause: (id: string) => void;
  resume: (id: string) => void;
  /** files brought back from before a reload, waiting for Resume */
  restored: number;
  /** take a file off the list; a half-downloaded part is removed from the folder */
  remove: (id: string) => void;
  pauseAll: () => void;
  resumeAll: () => void;
  cancelAll: () => void;
  clearFinished: () => void;
}

const DownloadCenterContext = createContext<Ctx | null>(null);

const SAMPLE_MS = 700;
const SMOOTHING = 0.3;
/** time between handing two files to the browser's own download manager */
const HANDOVER_GAP_MS = 900;
/** a broken transfer is retried by itself this many times, 2s, 4s, 8s ... apart */
const MAX_AUTO_RETRIES = 5;
/** files downloading at once into a picked folder */
const FOLDER_LANES = 3;

let seq = 0;
const newId = () => `d${Date.now().toString(36)}${(seq++).toString(36)}`;

// ---- kept across reloads: the queue and the folder handle, in IndexedDB (a big pull is
// thousands of rows, past what localStorage holds; a folder handle only clones into IDB)
const IDB_NAME = 'kaf-downloads';
const IDB_STORE = 'kv';
function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(IDB_NAME, 1);
    r.onupgradeneeded = () => { r.result.createObjectStore(IDB_STORE); };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
async function idbGet<T>(key: string): Promise<T | undefined> {
  try {
    const db = await idb();
    return await new Promise<T | undefined>((resolve) => {
      const r = db.transaction(IDB_STORE).objectStore(IDB_STORE).get(key);
      r.onsuccess = () => resolve(r.result as T | undefined);
      r.onerror = () => resolve(undefined);
    });
  } catch { return undefined; }   // private mode, blocked storage: the queue just does not survive
}
async function idbSet(key: string, value: unknown): Promise<void> {
  try {
    const db = await idb();
    await new Promise<void>((resolve) => {
      const t = db.transaction(IDB_STORE, 'readwrite');
      t.objectStore(IDB_STORE).put(value, key);
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
    });
  } catch { /* not kept; nothing else breaks */ }
}

/** A fresh signed link for this file, else the one it was queued with. */
async function freshUrl(job: DownloadJob): Promise<string> {
  try {
    const r = await api.post<{ id: string; url: string }[]>('/assets/download-links', { ids: [job.assetId] });
    return r.data.find((x) => x.id === job.assetId)?.url ?? job.url;
  } catch {
    return job.url;
  }
}

export function canPickFolder(): boolean {
  return typeof window !== 'undefined' && typeof (window as PickerWindow).showDirectoryPicker === 'function';
}

export function DownloadCenterProvider({ children }: { children: React.ReactNode }) {
  const [jobs, setJobs] = useState<DownloadJob[]>([]);
  const [folderName, setFolderName] = useState<string | null>(null);
  const batchRef = useRef(0);
  const jobsRef = useRef<DownloadJob[]>([]);
  jobsRef.current = jobs;
  const runningRef = useRef(0);
  const claimedRef = useRef<Set<string>>(new Set());
  const dirRef = useRef<FSDirHandle | null>(null);
  const abortRef = useRef<Map<string, AbortController>>(new Map());

  const patch = useCallback((id: string, next: Partial<DownloadJob>) => {
    setJobs((cur) => cur.map((j) => (j.id === id ? { ...j, ...next } : j)));
  }, []);

  const pickFolder = useCallback(async () => {
    const w = window as PickerWindow;
    if (typeof w.showDirectoryPicker !== 'function') return null;
    try {
      const dir = await w.showDirectoryPicker({ mode: 'readwrite' });
      dirRef.current = dir;
      setFolderName(dir.name);
      return dir;
    } catch {
      return null;   // cancelled, or refused: fall back to the browser's own downloads
    }
  }, []);

  /** A free name inside the folder: "x.mp4", then "x (2).mp4", never overwrite. */
  const freeName = useCallback(async (dir: FSDirHandle, name: string) => {
    const dot = name.lastIndexOf('.');
    const base = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : '';
    let candidate = name;
    for (let i = 2; i < 200; i++) {
      try { await dir.getFileHandle(candidate); } catch { return candidate; }
      candidate = `${base} (${i})${ext}`;
    }
    return candidate;
  }, []);

  // A break mid-file continues where it stopped. The part already on disk is
  // kept, the next attempt asks storage for the bytes after it (Range), and the
  // writer picks up at that position. It starts over only when the partial file
  // is gone or storage's ETag says the object changed underneath it.
  const runFolder = useCallback(async (job: DownloadJob, dir: FSDirHandle) => {
    const ac = new AbortController();
    abortRef.current.set(job.id, ac);
    patch(job.id, { state: 'downloading', bps: null, error: undefined, startedAt: job.startedAt ?? Date.now() });
    let writable: FSWritable | null = null;
    let name = job.savedName ?? '';
    try {
      let start = 0;
      if (name) {
        try { start = (await (await dir.getFileHandle(name)).getFile()).size; } catch { start = 0; name = ''; }
      }
      // Range is a safelisted header, so it needs no CORS preflight. If-Range is
      // not, and the bucket's CORS list does not carry it, so the version check is
      // done on the answer's ETag instead: a different object means start over.
      const url = await freshUrl(job);
      let res = await fetch(url, { signal: ac.signal, cache: 'no-store', headers: start > 0 ? { Range: `bytes=${start}-` } : {} });
      if (start > 0 && res.status === 206 && job.etag && res.headers.get('etag') && res.headers.get('etag') !== job.etag) {
        try { await res.body?.cancel(); } catch { /* fine */ }
        start = 0;
        res = await fetch(url, { signal: ac.signal, cache: 'no-store' });
      }
      if (!res.ok || !res.body) throw new Error(`storage answered ${res.status}`);
      const resumed = start > 0 && res.status === 206;
      if (!resumed) start = 0;   // a full answer: the partial was not honoured
      const etag = res.headers.get('etag') ?? job.etag;
      const total = resumed
        ? start + (Number(res.headers.get('content-length')) || (job.size - start))
        : (Number(res.headers.get('content-length')) || job.size);
      if (!name) name = await freeName(dir, job.name);
      const fh = await dir.getFileHandle(name, { create: true });
      writable = await fh.createWritable({ keepExistingData: resumed });
      if (resumed) await writable.seek(start);
      patch(job.id, { savedName: name, etag: etag ?? undefined, loaded: start, pct: total ? Math.min(99, Math.floor((start / total) * 100)) : 0 });
      const reader = res.body.getReader();
      let loaded = start, lastAt = Date.now(), lastLoaded = start, lastPct = -1;
      let bps: number | null = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        await writable.write(value);
        loaded += value.byteLength;
        const now = Date.now();
        const dt = now - lastAt;
        const pct = total ? Math.min(99, Math.floor((loaded / total) * 100)) : 0;
        if (dt >= SAMPLE_MS) {
          const sample = ((loaded - lastLoaded) * 1000) / dt;
          bps = bps === null ? sample : bps * (1 - SMOOTHING) + sample * SMOOTHING;
          lastAt = now; lastLoaded = loaded;
          patch(job.id, { pct, loaded, bps });
        } else if (pct !== lastPct) {
          patch(job.id, { pct, loaded });
        }
        lastPct = pct;
      }
      await writable.close();
      writable = null;
      patch(job.id, { state: 'done', pct: 100, loaded: total, bps: null, name, finishedAt: Date.now() });
    } catch (e) {
      const stopped = ac.signal.aborted;
      const paused = stopped && ac.signal.reason === 'pause';
      if (writable) {
        // Cancelled: throw the part away. Paused or broken: keep it, it is what the next go continues from.
        try { if (stopped && !paused) await writable.abort(); else await writable.close(); } catch { /* gone already */ }
      }
      if (paused) {
        patch(job.id, { state: 'paused', savedName: name || undefined, error: undefined, bps: null });
      } else if (stopped) {
        if (name) { try { await dir.removeEntry(name); } catch { /* not there */ } }
        patch(job.id, { state: 'cancelled', error: undefined, bps: null, pct: 0, loaded: 0, savedName: undefined, finishedAt: Date.now() });
      } else {
        const attempts = (job.attempts ?? 0) + 1;
        const msg = e instanceof Error ? e.message : 'Download failed';
        if (attempts <= MAX_AUTO_RETRIES) {
          const wait = Math.min(30000, 1000 * 2 ** attempts);
          patch(job.id, { state: 'queued', attempts, savedName: name || undefined, bps: null, notBefore: Date.now() + wait,
                          error: `${msg}; continuing in ${Math.round(wait / 1000)}s` });
          // Back off, then let the pump pick it up again. Still in order: it is the
          // first queued job, so nothing overtakes it.
          setTimeout(() => setJobs((cur) => [...cur]), wait);
        } else {
          patch(job.id, { state: 'failed', attempts, savedName: name || undefined, error: msg, bps: null, finishedAt: Date.now() });
        }
      }
    } finally {
      abortRef.current.delete(job.id);
      claimedRef.current.delete(job.id);
    }
  }, [patch, freeName]);

  const runBrowser = useCallback(async (job: DownloadJob) => {
    patch(job.id, { state: 'downloading', startedAt: Date.now() });
    try {
      const a = document.createElement('a');
      a.href = await freshUrl(job); a.rel = 'noreferrer';
      document.body.appendChild(a); a.click(); a.remove();
      await new Promise((r) => setTimeout(r, HANDOVER_GAP_MS));
      patch(job.id, { state: 'handed', pct: 100, loaded: job.size, finishedAt: Date.now() });
    } catch (e) {
      patch(job.id, { state: 'failed', error: e instanceof Error ? e.message : 'Could not start', finishedAt: Date.now() });
    } finally {
      claimedRef.current.delete(job.id);
    }
  }, [patch]);

  // One at a time, in the order queued. A job is claimed synchronously before any
  // await, so a second pump cannot pick it up.
  const pump = useCallback(() => {
    for (;;) {
      const now = Date.now();
      const next = jobsRef.current.find((j) => j.state === 'queued' && !claimedRef.current.has(j.id) && (j.notBefore ?? 0) <= now);
      if (!next) return;
      const dir = next.mode === 'folder' ? dirRef.current : null;
      if (next.mode === 'folder' && !dir) {
        // its folder is not open in this session yet: it waits for Resume, never falls
        // back to the browser's downloads (that would save a second copy elsewhere)
        setJobs((cur) => cur.map((j) => (j.id === next.id ? { ...j, state: 'paused', restored: true } : j)));
        return;
      }
      if (runningRef.current >= (dir ? FOLDER_LANES : 1)) return;
      claimedRef.current.add(next.id);
      runningRef.current += 1;
      const run = dir ? runFolder(next, dir) : runBrowser(next);
      void run.finally(() => {
        runningRef.current -= 1;
        pump();
      });
    }
  }, [runFolder, runBrowser]);

  useEffect(() => { pump(); }, [jobs, pump]);

  // When a run of the queue has settled, report how fast it really went (/transfers).
  const reportedRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const batches = new Set(jobs.map((j) => j.batch));
    for (const b of batches) {
      if (reportedRef.current.has(b)) continue;
      const run = jobs.filter((j) => j.batch === b);
      if (run.some((j) => j.state === 'queued' || j.state === 'downloading' || j.state === 'paused')) continue;
      reportedRef.current.add(b);
      reportTransfer('download', run.filter((j) => j.state === 'done' && j.mode === 'folder'), { maxLanes: FOLDER_LANES, mode: 'folder' });
    }
  }, [jobs]);

  // Bring back the queue of an earlier session: what was waiting or moving comes back
  // paused. Loaded once; saving starts only after it, so an empty first render never
  // overwrites what was kept.
  const loadedRef = useRef(false);
  const restoredDirRef = useRef<FSDirHandle | null>(null);
  useEffect(() => {
    let off = false;
    void (async () => {
      const kept = await idbGet<{ jobs: DownloadJob[]; folderName: string | null }>('queue');
      const dir = await idbGet<FSDirHandle>('dir');
      if (off) return;
      if (dir) restoredDirRef.current = dir;
      if (kept?.jobs?.length) {
        const back = kept.jobs.map((j) => (j.state === 'queued' || j.state === 'downloading'
          ? { ...j, state: 'paused' as DlState, bps: null, notBefore: 0, restored: true } : j));
        batchRef.current = Math.max(batchRef.current, ...back.map((j) => j.batch));
        setJobs((cur) => [...back, ...cur]);
        if (kept.folderName) setFolderName((f) => f ?? kept.folderName);
      }
      loadedRef.current = true;
    })();
    return () => { off = true; };
  }, []);
  // Kept as it changes, at most once a second; byte counters move many times a second.
  useEffect(() => {
    if (!loadedRef.current) return;
    const t = setTimeout(() => { void idbSet('queue', { jobs: jobsRef.current, folderName }); }, 1000);
    return () => clearTimeout(t);
  }, [jobs, folderName]);

  /** The folder for jobs that came back from an earlier session. Asking for permission
   *  needs the click, so this runs first thing in Resume. */
  const ensureDir = useCallback(async (): Promise<boolean> => {
    if (dirRef.current) return true;
    const kept = restoredDirRef.current;
    if (kept?.requestPermission) {
      try {
        if ((await kept.requestPermission({ mode: 'readwrite' })) === 'granted') {
          dirRef.current = kept; setFolderName(kept.name); return true;
        }
      } catch { /* refused or gone: ask for the folder instead */ }
    }
    const w = window as PickerWindow;
    if (typeof w.showDirectoryPicker !== 'function') return false;
    try {
      const dir = await w.showDirectoryPicker({ mode: 'readwrite' });
      dirRef.current = dir; setFolderName(dir.name); void idbSet('dir', dir);
      return true;
    } catch { return false; }
  }, []);

  const enqueue = useCallback((items: EnqueueDownload[], dir?: FSDirHandle | null) => {
    if (!items.length) return 0;
    if (dir) { dirRef.current = dir; setFolderName(dir.name); void idbSet('dir', dir); }
    const mode: DlMode = dir ? 'folder' : 'browser';
    const now = Date.now();
    const stillGoing = jobsRef.current.some((j) => j.state === 'queued' || j.state === 'downloading');
    if (!stillGoing) batchRef.current += 1;
    const batch = batchRef.current;
    const added: DownloadJob[] = items.map((it) => ({
      id: newId(), batch, mode,
      assetId: it.assetId, creativeId: it.creativeId,
      creativeCode: it.creativeCode ?? '', creativeTitle: it.creativeTitle ?? '',
      kind: it.kind, category: it.category,
      name: it.name, size: it.size, url: it.url,
      state: 'queued', pct: 0, loaded: 0, bps: null, queuedAt: now, attempts: 0,
    }));
    setJobs((cur) => [...cur, ...added]);
    return added.length;
  }, []);

  // A manual retry continues from the part on disk (savedName stays); a cancelled
  // job starts over, its part was thrown away.
  const retry = useCallback((id: string) => {
    setJobs((cur) => cur.map((j) => (
      j.id === id && (j.state === 'failed' || j.state === 'cancelled')
        ? { ...j, state: 'queued', attempts: 0, notBefore: 0, error: undefined, finishedAt: undefined,
            ...(j.state === 'cancelled' ? { pct: 0, loaded: 0, savedName: undefined, startedAt: undefined } : {}) }
        : j
    )));
  }, []);

  const retryAllFailed = useCallback(() => {
    setJobs((cur) => cur.map((j) => (
      j.state === 'failed'
        ? { ...j, state: 'queued', attempts: 0, notBefore: 0, error: undefined, finishedAt: undefined }
        : j
    )));
  }, []);

  // A download in flight stops through its fetch: pause keeps the part on disk,
  // cancel throws it away. One not moving changes state at once.
  const dropPart = useCallback(async (name?: string) => {
    const dir = dirRef.current;
    if (dir && name) { try { await dir.removeEntry(name); } catch { /* not there */ } }
  }, []);
  const stop = useCallback((ids: string[], how: 'pause' | 'cancel') => {
    const idle = new Set<string>();
    for (const id of ids) {
      const ac = abortRef.current.get(id);
      if (ac) ac.abort(how); else idle.add(id);
    }
    if (!idle.size) return;
    if (how === 'cancel') {
      for (const j of jobsRef.current) if (idle.has(j.id) && j.state !== 'done') void dropPart(j.savedName);
    }
    setJobs((cur) => cur.map((j) => {
      if (!idle.has(j.id)) return j;
      if (how === 'pause') return j.state === 'queued' ? { ...j, state: 'paused', notBefore: 0 } : j;
      return (j.state === 'queued' || j.state === 'paused' || j.state === 'failed')
        ? { ...j, state: 'cancelled', pct: 0, loaded: 0, savedName: undefined, finishedAt: Date.now() } : j;
    }));
  }, [dropPart]);
  const cancel = useCallback((id: string) => stop([id], 'cancel'), [stop]);
  const pause = useCallback((id: string) => stop([id], 'pause'), [stop]);
  const resume = useCallback((id: string) => {
    void (async () => {
      const j = jobsRef.current.find((x) => x.id === id);
      if (j?.mode === 'folder' && !(await ensureDir())) return;
      setJobs((cur) => cur.map((x) => (x.id === id && x.state === 'paused' ? { ...x, state: 'queued', attempts: 0, notBefore: 0, restored: false } : x)));
    })();
  }, [ensureDir]);
  // Off the list. A finished file stays on the computer; a half-downloaded part does not.
  const remove = useCallback((id: string) => {
    const ac = abortRef.current.get(id);
    if (ac) ac.abort('cancel');
    else {
      const j = jobsRef.current.find((x) => x.id === id);
      if (j && j.state !== 'done' && j.state !== 'handed') void dropPart(j.savedName);
    }
    setJobs((cur) => cur.filter((j) => j.id !== id));
  }, [dropPart]);
  const pauseAll = useCallback(() => {
    stop(jobsRef.current.filter((j) => j.state === 'queued' || (j.state === 'downloading' && j.mode === 'folder')).map((j) => j.id), 'pause');
  }, [stop]);
  const resumeAll = useCallback(() => {
    void (async () => {
      const needsDir = jobsRef.current.some((j) => j.state === 'paused' && j.mode === 'folder');
      const ok = !needsDir || await ensureDir();
      setJobs((cur) => cur.map((j) => (j.state === 'paused' && (ok || j.mode !== 'folder')
        ? { ...j, state: 'queued', attempts: 0, notBefore: 0, restored: false } : j)));
    })();
  }, [ensureDir]);
  const cancelAll = useCallback(() => {
    stop(jobsRef.current.filter((j) => j.state === 'queued' || j.state === 'paused' || (j.state === 'downloading' && j.mode === 'folder')).map((j) => j.id), 'cancel');
  }, [stop]);

  const clearFinished = useCallback(() => {
    setJobs((cur) => cur.filter((j) => j.state === 'queued' || j.state === 'downloading'));
  }, []);

  const active = jobs.filter((j) => j.state === 'downloading').length;
  const queued = jobs.filter((j) => j.state === 'queued').length;
  const paused = jobs.filter((j) => j.state === 'paused').length;
  const failed = jobs.filter((j) => j.state === 'failed').length;
  const done = jobs.filter((j) => j.state === 'done' || j.state === 'handed').length;
  const restored = jobs.filter((j) => j.state === 'paused' && j.restored).length;

  useEffect(() => {
    if (active + queued === 0) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active, queued]);

  const value = useMemo<Ctx>(() => {
    const live = jobs.filter((j) => j.state === 'queued' || j.state === 'downloading');
    const pendingBytes = live.reduce((n, j) => n + j.size * (1 - j.pct / 100), 0);
    const run = jobs.filter((j) => j.batch === batchRef.current && j.state !== 'cancelled');
    const totalBytes = run.reduce((n, j) => n + j.size, 0);
    const gotBytes = run.reduce((n, j) => n + j.size * ((j.state === 'done' || j.state === 'handed') ? 1 : j.pct / 100), 0);
    const pct = totalBytes ? Math.round((gotBytes / totalBytes) * 100) : 0;
    const moving = jobs.filter((j) => j.state === 'downloading' && j.bps !== null);
    const bytesPerSec = moving.length ? moving.reduce((n, j) => n + (j.bps ?? 0), 0) : null;
    const etaSeconds = bytesPerSec && bytesPerSec > 0 && pendingBytes > 0 ? Math.round(pendingBytes / bytesPerSec) : null;
    return {
      jobs, active, queued, paused, failed, done, pendingBytes, restored,
      doneThisRun: run.filter((j) => j.state === 'done' || j.state === 'handed').length,
      totalThisRun: run.length,
      bytesPerSec, etaSeconds,
      overallPct: live.length ? Math.min(99, pct) : null,
      folderName, canPickFolder: canPickFolder(),
      pickFolder, enqueue, retry, retryAllFailed, cancel, pause, resume, remove, pauseAll, resumeAll, cancelAll, clearFinished,
    };
  }, [jobs, active, queued, paused, failed, done, restored, folderName, pickFolder, enqueue, retry, retryAllFailed, cancel, pause, resume, remove, pauseAll, resumeAll, cancelAll, clearFinished]);

  return <DownloadCenterContext.Provider value={value}>{children}</DownloadCenterContext.Provider>;
}

/** false outside the staff app (the portal has no Download Center) */
export function useHasDownloadCenter(): boolean {
  return useContext(DownloadCenterContext) !== null;
}

export function useDownloadCenter(): Ctx {
  const ctx = useContext(DownloadCenterContext);
  return ctx ?? FALLBACK;
}

const FALLBACK: Ctx = {
  jobs: [], active: 0, queued: 0, paused: 0, failed: 0, done: 0, restored: 0, pendingBytes: 0, overallPct: null,
  doneThisRun: 0, totalThisRun: 0, bytesPerSec: null, etaSeconds: null,
  folderName: null, canPickFolder: false,
  pickFolder: async () => null, enqueue: () => 0, retry: () => {},
  retryAllFailed: () => {}, cancel: () => {}, pause: () => {}, resume: () => {}, remove: () => {},
  pauseAll: () => {}, resumeAll: () => {}, cancelAll: () => {}, clearFinished: () => {},
};
